import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Filter, Grid, List, Calendar, MapPin, Users, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Mock project data - replace with actual data source
const mockProjects = [
  {
    id: 1,
    title: "Smart Grid Infrastructure",
    description: "Advanced power distribution system with IoT integration for enhanced efficiency and monitoring capabilities.",
    category: "Power Systems",
    location: "Dubai, UAE",
    date: "2024",
    status: "Completed",
    team: "15 Engineers",
    thumbnail: "/placeholder-image.svg",
    tags: ["IoT", "Smart Grid", "Power Distribution"]
  },
  {
    id: 2,
    title: "Telecommunications Network Expansion",
    description: "Large-scale fiber optic network deployment across multiple emirates with 5G infrastructure support.",
    category: "Telecommunications",
    location: "Abu Dhabi, UAE",
    date: "2024",
    status: "In Progress",
    team: "22 Engineers",
    thumbnail: "/placeholder-image.svg",
    tags: ["5G", "Fiber Optic", "Network Infrastructure"]
  },
  {
    id: 3,
    title: "Hazardous Area Safety Systems",
    description: "Implementation of advanced safety protocols and monitoring systems for oil and gas facilities.",
    category: "Hazardous Areas",
    location: "Sharjah, UAE",
    date: "2023",
    status: "Completed",
    team: "18 Engineers",
    thumbnail: "/placeholder-image.svg",
    tags: ["Safety Systems", "Oil & Gas", "Monitoring"]
  },
  {
    id: 4,
    title: "Solar Power Plant Integration",
    description: "Renewable energy integration project with advanced battery storage and grid synchronization.",
    category: "Renewable Energy",
    location: "Al Ain, UAE",
    date: "2024",
    status: "Planning",
    team: "12 Engineers",
    thumbnail: "/placeholder-image.svg",
    tags: ["Solar Power", "Battery Storage", "Grid Integration"]
  },
  {
    id: 5,
    title: "Industrial Automation Suite",
    description: "Complete automation solution for manufacturing facilities with AI-powered optimization.",
    category: "Industrial",
    location: "Ajman, UAE",
    date: "2023",
    status: "Completed",
    team: "20 Engineers",
    thumbnail: "/placeholder-image.svg",
    tags: ["Automation", "AI", "Manufacturing"]
  },
  {
    id: 6,
    title: "Data Center Infrastructure",
    description: "State-of-the-art data center with advanced cooling systems and redundant power supplies.",
    category: "Data Centers",
    location: "Dubai, UAE",
    date: "2024",
    status: "In Progress",
    team: "25 Engineers",
    thumbnail: "/placeholder-image.svg",
    tags: ["Data Center", "Cooling Systems", "Power Redundancy"]
  }
];

const Projects = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedStatus, setSelectedStatus] = useState('All');
  const [viewMode, setViewMode] = useState('grid');
  const [filteredProjects, setFilteredProjects] = useState(mockProjects);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const categories = ['All', 'Power Systems', 'Telecommunications', 'Hazardous Areas', 'Renewable Energy', 'Industrial', 'Data Centers'];
  const statuses = ['All', 'Completed', 'In Progress', 'Planning'];

  useEffect(() => {
    let filtered = mockProjects;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(project =>
        project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Filter by category
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(project => project.category === selectedCategory);
    }

    // Filter by status
    if (selectedStatus !== 'All') {
      filtered = filtered.filter(project => project.status === selectedStatus);
    }

    setFilteredProjects(filtered);
  }, [searchTerm, selectedCategory, selectedStatus]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed':
        return 'bg-green-100 text-green-800';
      case 'In Progress':
        return 'bg-blue-100 text-blue-800';
      case 'Planning':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const ProjectCard = ({ project, index }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border border-gray-100"
    >
      {/* Gradient overlay on hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-global-green/5 to-global-light-blue/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10" />
      
      {/* Image container */}
      <div className="relative h-48 overflow-hidden">
        <img
          src={project.thumbnail}
          alt={project.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        
        {/* Status badge */}
        <div className={`absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(project.status)}`}>
          {project.status}
        </div>
      </div>

      {/* Content */}
      <div className="relative z-20 p-6">
        <div className="mb-3">
          <span className="text-sm font-medium text-global-green bg-global-green/10 px-3 py-1 rounded-full">
            {project.category}
          </span>
        </div>

        <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-global-green transition-colors duration-300">
          {project.title}
        </h3>

        <p className="text-gray-600 mb-4 line-clamp-3">
          {project.description}
        </p>

        {/* Project details */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center text-sm text-gray-500">
            <MapPin className="w-4 h-4 mr-2" />
            {project.location}
          </div>
          <div className="flex items-center text-sm text-gray-500">
            <Calendar className="w-4 h-4 mr-2" />
            {project.date}
          </div>
          <div className="flex items-center text-sm text-gray-500">
            <Users className="w-4 h-4 mr-2" />
            {project.team}
          </div>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-4">
          {project.tags.slice(0, 3).map((tag, tagIndex) => (
            <span
              key={tagIndex}
              className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-md"
            >
              {tag}
            </span>
          ))}
        </div>

        {/* View details button */}
        <Link
          to={`/projects/${project.id}`}
          className="inline-flex items-center text-global-green hover:text-global-light-blue font-semibold transition-colors duration-300 group/link"
        >
          View Details
          <ArrowRight className="w-4 h-4 ml-2 transition-transform duration-300 group-hover/link:translate-x-1" />
        </Link>
      </div>
    </motion.div>
  );

  const ProjectListItem = ({ project, index }) => (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="group bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 p-6 border border-gray-100"
    >
      <div className="flex items-center space-x-6">
        <div className="flex-shrink-0">
          <img
            src={project.thumbnail}
            alt={project.title}
            className="w-24 h-24 object-cover rounded-lg"
          />
        </div>
        
        <div className="flex-grow">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center space-x-3 mb-2">
                <h3 className="text-lg font-bold text-gray-900 group-hover:text-global-green transition-colors duration-300">
                  {project.title}
                </h3>
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(project.status)}`}>
                  {project.status}
                </span>
              </div>
              
              <p className="text-gray-600 mb-3 line-clamp-2">
                {project.description}
              </p>
              
              <div className="flex items-center space-x-4 text-sm text-gray-500">
                <div className="flex items-center">
                  <MapPin className="w-4 h-4 mr-1" />
                  {project.location}
                </div>
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 mr-1" />
                  {project.date}
                </div>
                <div className="flex items-center">
                  <Users className="w-4 h-4 mr-1" />
                  {project.team}
                </div>
              </div>
            </div>
            
            <Link
              to={`/projects/${project.id}`}
              className="flex-shrink-0 bg-gradient-to-r from-global-green to-global-light-blue text-white px-4 py-2 rounded-lg hover:shadow-lg transition-all duration-300 transform hover:scale-105"
            >
              View Details
            </Link>
          </div>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-global-green to-global-light-blue text-white py-60">
        <div className="absolute inset-0 bg-black/10" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Our Projects
            </h1>
            <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto">
              Discover our innovative engineering solutions across various industries and technologies
            </p>
          </motion.div>
        </div>
      </div>

      {/* Search and Filter Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white rounded-2xl shadow-lg p-6 mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-6">
            {/* Search */}
            <div className="relative flex-grow max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-global-green focus:border-transparent transition-all duration-300"
              />
            </div>

            {/* Filters */}
            <div className="flex items-center space-x-4">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-global-green focus:border-transparent transition-all duration-300"
              >
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>

              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-global-green focus:border-transparent transition-all duration-300"
              >
                {statuses.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>

              {/* View Mode Toggle */}
              <div className="flex bg-gray-100 rounded-xl p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg transition-all duration-300 ${
                    viewMode === 'grid'
                      ? 'bg-white shadow-md text-global-green'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Grid className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg transition-all duration-300 ${
                    viewMode === 'list'
                      ? 'bg-white shadow-md text-global-green'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <List className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Results count */}
          <div className="mt-4 text-sm text-gray-600">
            Showing {filteredProjects.length} of {mockProjects.length} projects
          </div>
        </motion.div>

        {/* Projects Grid/List */}
        <AnimatePresence mode="wait">
          {viewMode === 'grid' ? (
            <motion.div
              key="grid"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {filteredProjects.map((project, index) => (
                <ProjectCard key={project.id} project={project} index={index} />
              ))}
            </motion.div>
          ) : (
            <motion.div
              key="list"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              {filteredProjects.map((project, index) => (
                <ProjectListItem key={project.id} project={project} index={index} />
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* No results */}
        {filteredProjects.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <div className="text-gray-400 mb-4">
              <Search className="w-16 h-16 mx-auto" />
            </div>
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No projects found</h3>
            <p className="text-gray-500">Try adjusting your search criteria or filters</p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Projects;